import React, { useEffect, useState, useMemo } from "react";
import { useTranslation } from "react-i18next";
import axios from "axios";
import { FiSearch } from "react-icons/fi";
import { BiArrowBack, BiStar } from "react-icons/bi";
import { NavLink, useNavigate } from "react-router-dom";
import { MdOutlineGridView, MdViewList } from "react-icons/md";
import apiKey from "../../../config/apiKey";

const OnlineTeacherList = () => {
  const { t } = useTranslation();
  const [isGridView, setIsGridView] = useState(true);
  const [lessons, setLessons] = useState([]);
  const [searchTerm, setSearchTerm] = useState("");
  const [maxFee, setMaxFee] = useState(5000);
  const [error, setError] = useState(null);
  const student = JSON.parse(localStorage.getItem("edo-student"));
  const [loading, setLoading] = useState(true);

  const navigate = useNavigate();

  useEffect(() => {
    setLoading(true);
    axios
      .get(`${apiKey}/api/edolutions/student/show-data`, {
        headers: { Authorization: `Bearer ${student?.token}` },
      })
      .then((res) => {
        if (res.data?.data?.length) setLessons(res.data.data);
        else setError(t("studentDash.onlineTeacherList.noTeacherFound"));
      })
      .catch(() => setError(t("studentDash.onlineTeacherList.noTeacherFound")))
      .finally(() => setLoading(false));
  }, [student?.token]);

  const filteredLessons = useMemo(
    () =>
      lessons.filter(
        (teacherItem) =>
          (teacherItem.name || "")
            .toLowerCase()
            .includes(searchTerm.trim().toLowerCase()) &&
          Number(teacherItem.online_charges) <= Number(maxFee)
      ),
    [lessons, searchTerm, maxFee]
  );

  const SkeletonCard = () => (
    <div className="rounded-2xl border border-gray-100 bg-white p-4 shadow-sm">
      <div className="flex items-center gap-4">
        <div className="h-16 w-16 rounded-full bg-gray-100 animate-pulse" />
        <div className="flex-1 space-y-2">
          <div className="h-4 w-40 bg-gray-100 rounded animate-pulse" />
          <div className="h-3 w-28 bg-gray-100 rounded animate-pulse" />
          <div className="h-3 w-20 bg-gray-100 rounded animate-pulse" />
        </div>
        <div className="h-4 w-24 bg-gray-100 rounded animate-pulse" />
      </div>
    </div>
  );

  return (
    <div className="w-full">
      {/* Themed header */}
      <div className="relative overflow-hidden rounded-3xl ">

        <div className="relative z-10 flex items-center justify-start gap-8 p-4 sm:p-6">
          <button
            onClick={() => navigate(-1)}
         className="inline-flex items-center gap-2 rounded-xl  px-3 py-2 text-white text-sm font-medium bg-gradient-to-r from-[#EE5F2B] to-[#2A3A54] active:scale-95 transition"
          >
            <BiArrowBack className="h-5 w-5" />
            {t("studentDash.common.back")}
          </button>

          <div className=" text-black">
            <p className="text-lg sm:text-xl font-semibold">{t("studentDash.onlineTeacherList.findMentors")}</p>
            <p className="text-xs sm:text-sm opacity-90">{t("studentDash.onlineTeacherList.onlineSessions")}</p>
          </div>
        </div>
      </div>

      {/* Controls */}
      <div className="mt-6 rounded-3xl border border-gray-100 bg-white/80 backdrop-blur p-4 sm:p-5 shadow-sm">
        <div className="flex flex-col gap-4 md:flex-row md:items-center md:justify-between">
          {/* Search */}
          <div className="w-full md:max-w-md">
            <div className="relative">
              <FiSearch className="absolute left-3 top-1/2 -translate-y-1/2 text-gray-400 h-5 w-5" />
              <input
                type="text"
                placeholder={t("studentDash.onlineTeacherList.searchByMentorName")}
                value={searchTerm}
                onChange={(e) => setSearchTerm(e.target.value)}
                className="w-full rounded-xl border border-gray-200 bg-white pl-10 pr-3 py-2.5 text-sm outline-none focus:border-[#2A3A54] focus:ring-2 focus:ring-[#EE5F2B]/30"
              />
            </div>
          </div>

          {/* Fee slider + view toggle */}
          <div className="flex flex-col-reverse gap-4 sm:flex-row sm:items-center sm:justify-end w-full md:w-auto">
            <div className="flex items-center gap-3">
              <span className="text-sm text-gray-600 whitespace-nowrap">
                {t("studentDash.onlineTeacherList.maxFee")} <b>Rs.{maxFee}</b>
              </span>
              <input
                type="range"
                min="500"
                max="5000"
                value={maxFee}
                onChange={(e) => setMaxFee(e.target.value)}
                className="w-40 accent-[#EE5F2B]"
              />
            </div>

            <div className="flex items-center gap-2 self-end sm:self-auto">
              <button
                onClick={() => setIsGridView(true)}
                className={`inline-flex items-center justify-center h-10 w-10 rounded-xl border transition ${
                  isGridView
                    ? "border-[#EE5F2B] text-[#EE5F2B] bg-[#EE5F2B]/5"
                    : "border-gray-200 text-gray-600 hover:bg-gray-50"
                }`}
                title={t("studentDash.onlineTeacherList.gridView")}
              >
                <MdOutlineGridView className="h-5 w-5" />
              </button>
              <button
                onClick={() => setIsGridView(false)}
                className={`inline-flex items-center justify-center h-10 w-10 rounded-xl border transition ${
                  !isGridView
                    ? "border-[#EE5F2B] text-[#EE5F2B] bg-[#EE5F2B]/5"
                    : "border-gray-200 text-gray-600 hover:bg-gray-50"
                }`}
                title={t("studentDash.onlineTeacherList.listView")}
              >
                <MdViewList className="h-5 w-5" />
              </button>
            </div>
          </div>
        </div>
      </div>

      {/* Results */}
      <div className="mt-6">
        {loading ? (
          <div
            className={
              isGridView
                ? "grid grid-cols-1 md:grid-cols-2 gap-4"
                : "flex flex-col gap-4"
            }
          >
            {Array.from({ length: 4 }).map((_, i) => (
              <SkeletonCard key={i} />
            ))}
          </div>
        ) : error ? (
          <div className="rounded-2xl border border-dashed border-gray-200 bg-gray-50 p-8 text-center">
            <p className="text-gray-800 font-semibold">{t("studentDash.onlineTeacherList.noMentorsFound")}</p>
            <p className="text-gray-500 text-sm mt-1">{error}</p>
          </div>
        ) : filteredLessons.length === 0 ? (
          <div className="rounded-2xl border border-dashed border-gray-200 bg-gray-50 p-8 text-center">
            <p className="text-gray-800 font-semibold">
              {t("studentDash.onlineTeacherList.noMentorsInRange")}
            </p>
            <p className="text-gray-500 text-sm mt-1">
              {t("studentDash.onlineTeacherList.tryIncreasingFee")}
            </p>
          </div>
        ) : (
          <div
            className={
              isGridView
                ? "grid grid-cols-1 md:grid-cols-2 gap-4"
                : "flex flex-col gap-4"
            }
          >
            {filteredLessons.map((teacher) => (
              <div
                key={teacher.id}
                className="group rounded-2xl border border-gray-100 bg-white p-4 shadow-sm hover:shadow-md transition"
              >
                <div className="flex flex-col md:flex-row md:items-center gap-4">
                  {/* Avatar */}
                  <img
                    src={
                      teacher.photo
                        ? `${apiKey}/userProfile/${teacher.photo}`
                        : `${apiKey}/userProfile/87654321.png`
                    }
                    alt={teacher.name || t("studentDash.onlineTeacherList.teacher")}
                    className="h-16 w-16 rounded-full object-cover ring-1 ring-gray-200"
                  />

                  {/* Info */}
                  <NavLink
                    to={`../mentor-portfolio/${teacher.user_id}`}
                    className="flex-1 min-w-0"
                  >
                    <h3 className="truncate text-lg font-semibold capitalize text-[#2A3A54]">
                      {teacher.name}
                    </h3>
                    <p className="truncate text-xs text-gray-600">
                      {teacher.subjects}
                    </p>

                    <div className="mt-2 flex flex-wrap items-center gap-2">
                      <div className="flex items-center">
                        <BiStar className="h-4 w-4 text-yellow-500" />
                        <BiStar className="h-4 w-4 text-yellow-500" />
                        <BiStar className="h-4 w-4 text-yellow-500" />
                      </div>
                      <span className="inline-flex items-center rounded-full border border-gray-200 bg-gray-50 px-2.5 py-1 text-[11px] font-medium text-gray-700">
                        {t("studentDash.onlineTeacherList.oneHourSession")}
                      </span>
                    </div>
                  </NavLink>

                  {/* Price */}
                  <div className="text-center md:text-right">
                    <p className="text-sm font-semibold text-[#2A3A54]">
                      Rs.{teacher.online_charges}
                      <span className="text-gray-500 font-normal"> {t("studentDash.onlineTeacherList.perHourSuffix")}</span>
                    </p>
                    <div className="mt-2">
                      <NavLink
                        to={`../mentor-portfolio/${teacher.user_id}`}
                        className="inline-block rounded-lg bg-gradient-to-r from-[#EE5F2B] to-[#2A3A54] px-3 py-1.5 text-white text-xs font-semibold hover:brightness-110 active:scale-95 transition"
                      >
                        {t("studentDash.onlineTeacherList.viewProfile")}
                      </NavLink>
                    </div>
                  </div>
                </div>

                {/* Subtle glow on hover */}
                <div className="pointer-events-none mt-3 h-2 bg-gradient-to-r from-[#EE5F2B]/10 to-[#2A3A54]/10 blur-lg opacity-0 group-hover:opacity-100 transition" />
              </div>
            ))}
          </div>
        )}
      </div>
    </div>
  );
};

export default OnlineTeacherList;
